import React, { useEffect, useId } from 'react';

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select } from '@mui/material';

import {
  IndicativePropertyStateFilterOption,
  getIndicativePropertyStateFilterToken,
  normalizeIndicativePropertyStateFilters,
} from './indicativePropertyStateFilters';

interface Props {
  label: string;
  options: Array<IndicativePropertyStateFilterOption>;
  value: string;
  onChange: (newValue: string) => void;
}

export default function AccommodationIndicativePropertyStateFilterInput({ label, options, value, onChange }: Props) {
  const inputId = useId();
  const labelId = `${inputId}-label`;
  const selectId = `${inputId}-select`;
  const normalizedValue = normalizeIndicativePropertyStateFilters(value);
  const sanitizedValue = normalizeIndicativePropertyStateFilters(value, options);
  const selected = sanitizedValue.split(',').filter(Boolean);

  useEffect(() => {
    if (sanitizedValue !== normalizedValue) {
      onChange(sanitizedValue);
    }
  }, [normalizedValue, onChange, sanitizedValue]);

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<Array<string>>
        id={selectId}
        labelId={labelId}
        label={label}
        multiple
        value={selected}
        onChange={(e) => {
          const next = e.target.value as Array<string>;
          onChange(normalizeIndicativePropertyStateFilters(next.join(','), options));
        }}
        renderValue={(tokens: Array<string>) =>
          tokens.length === 0
            ? 'All'
            : tokens
                .map(
                  (token) => options.find((option) => getIndicativePropertyStateFilterToken(option) === token)?.label,
                )
                .filter(Boolean)
                .join(', ')
        }
      >
        {options.map((option) => {
          const token = getIndicativePropertyStateFilterToken(option);
          return (
            <MenuItem key={token} value={token}>
              <Checkbox checked={selected.includes(token)} size="small" />
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
